import { RangeSet, StateEffect, StateField } from '@codemirror/state';
import { Decoration, EditorView, GutterMarker, ViewPlugin, gutterLineClass } from '@codemirror/view';

import {
  buildLineMap,
  extractAllAssigns,
  extractAllLoops,
  getActiveLoopsAtPosition,
  getLineNumber,
  resolveFieldPath,
} from '../liquid-syntax-utils';
import { buildValidPathsFromSchema } from '../schema-utils';
import { ValidationErrorType } from '../types';

import type { HtmlValidationError, TemplateSchema } from '../types';
import type { Extension } from '@codemirror/state';
import type { DecorationSet, ViewUpdate } from '@codemirror/view';

type HtmlValidationConfig = {
  onValidationChange: (error: HtmlValidationError | null) => void;
  schema: TemplateSchema;
};

const VALIDATION_DELAY_MS = 2000;

const VOID_ELEMENTS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

const PDF_UNSUPPORTED_ELEMENTS = new Set(['script', 'iframe', 'video', 'audio', 'form', 'input', 'select']);

const EVENT_HANDLER_PATTERN = /\b(on[a-z]+)\s*=/i;

const TEMPLATE_EXPRESSION_PATTERN = /\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\}|\{#[\s\S]*?#\}/g;
const HTML_COMMENT_PATTERN = /<!--[\s\S]*?-->/g;
const DOCTYPE_PATTERN = /<!DOCTYPE[^>]*>/gi;

const STYLE_SCRIPT_BLOCK_PATTERN = /<(style|script)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;

const BALANCED_LIQUID_PATTERN = /\{\{[^{}]*\}\}|\{%[^{}]*%\}|\{#[^{}]*#\}/g;
const UNCLOSED_LIQUID_OPENER_PATTERN = /\{[{%#]/;

const replacePreservingNewlines = (text: string, pattern: RegExp): string => text.replace(pattern, (match) => match.replace(/[^\n]/g, ' '));

// Blank out <style>/<script> bodies, HTML comments, and balanced Liquid;
// any remaining `{{`, `{%`, or `{#` is by definition unclosed.
const findUnclosedLiquidDelimiter = (content: string, lineMap: number[]): HtmlValidationError | null => {
  let sanitized = replacePreservingNewlines(content, STYLE_SCRIPT_BLOCK_PATTERN);
  sanitized = replacePreservingNewlines(sanitized, HTML_COMMENT_PATTERN);
  sanitized = replacePreservingNewlines(sanitized, BALANCED_LIQUID_PATTERN);

  const match = UNCLOSED_LIQUID_OPENER_PATTERN.exec(sanitized);
  if (!match) {
    return null;
  }

  return {
    line: getLineNumber(lineMap, match.index),
    type: ValidationErrorType.MalformedLiquid,
    detail: `{${sanitized[match.index + 1]}`,
  };
};

export const validateHtml = (content: string, schema: TemplateSchema): HtmlValidationError | null => {
  if (!content.trim()) {
    return null;
  }

  let firstFieldError: HtmlValidationError | null = null;

  const validPaths = buildValidPathsFromSchema(schema);

  // Build line map once for all line number lookups (major performance optimization)
  const lineMap = buildLineMap(content);

  // Short-circuit: downstream sanitizer can pair an unclosed `{{` with a later
  // `}}` and corrupt the HTML scan, so fix the Liquid first.
  const malformedLiquidError = findUnclosedLiquidDelimiter(content, lineMap);
  if (malformedLiquidError) {
    return malformedLiquidError;
  }

  // Schema-driven validation: extract all loops and assigns once, then validate each field
  if (validPaths.size > 0) {
    const loops = extractAllLoops(content, lineMap);
    const assigns = extractAllAssigns(content, lineMap);
    const fieldPattern = /\{\{\s*([^}]+?)\s*\}\}/g;
    let fieldMatch;

    // Performance optimization: use moving index instead of rebuilding Set for every field
    // Assigns are already sorted by position, so we can track active assigns incrementally
    let assignIndex = 0;
    const activeAssignNames = new Set<string>();

    while ((fieldMatch = fieldPattern.exec(content)) !== null) {
      // Update active assigns: add all assigns that come before this field
      while (assignIndex < assigns.length && assigns[assignIndex].position < fieldMatch.index) {
        activeAssignNames.add(assigns[assignIndex].variableName);
        assignIndex++;
      }

      const fieldExpression = fieldMatch[1].trim();
      const pathWithoutFilters = fieldExpression.split('|')[0].trim();

      if (pathWithoutFilters) {
        // Check if this is an assigned variable (only simple variable names, not paths)
        const isSimpleVariable = !pathWithoutFilters.includes('.');
        if (isSimpleVariable && activeAssignNames.has(pathWithoutFilters)) {
          // Valid assigned variable, skip schema validation
          continue;
        }

        // Not an assigned variable, validate against schema
        const activeLoops = getActiveLoopsAtPosition(loops, fieldMatch.index);
        const resolvedPath = resolveFieldPath(pathWithoutFilters, activeLoops);

        if (!validPaths.has(resolvedPath)) {
          const lineNumber = getLineNumber(lineMap, fieldMatch.index);
          firstFieldError = { line: lineNumber, type: ValidationErrorType.InvalidField, detail: pathWithoutFilters };
          break;
        }
      }
    }
  }

  let sanitized = replacePreservingNewlines(content, TEMPLATE_EXPRESSION_PATTERN);
  sanitized = replacePreservingNewlines(sanitized, HTML_COMMENT_PATTERN);
  sanitized = replacePreservingNewlines(sanitized, DOCTYPE_PATTERN);

  const tagPattern = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*\/?>/g;
  const stack: { tag: string; line: number }[] = [];
  let firstPrintError: HtmlValidationError | null = null;
  let match;

  while ((match = tagPattern.exec(sanitized)) !== null) {
    const fullMatch = match[0];
    const tagName = match[1].toLowerCase();
    const isClosing = fullMatch.startsWith('</');
    const isSelfClosing = fullMatch.endsWith('/>');
    const lineNumber = getLineNumber(lineMap, match.index);

    // Check for PDF-unsupported elements and event handlers (only on opening tags)
    if (!firstPrintError && !isClosing) {
      const isUnsupportedElement = PDF_UNSUPPORTED_ELEMENTS.has(tagName);
      const eventHandlerMatch = isUnsupportedElement ? null : fullMatch.match(EVENT_HANDLER_PATTERN);
      const detail = isUnsupportedElement ? `<${tagName}>` : eventHandlerMatch?.[1];
      const type = isUnsupportedElement ? ValidationErrorType.UnsupportedElement : ValidationErrorType.UnsupportedAttribute;

      if (detail) {
        firstPrintError = { line: lineNumber, type, detail };
      }
    }

    if (VOID_ELEMENTS.has(tagName) || isSelfClosing) {
      continue;
    }

    if (isClosing) {
      if (stack.length === 0) {
        return { line: lineNumber, type: ValidationErrorType.Syntax };
      }
      const last = stack[stack.length - 1];
      if (last.tag !== tagName) {
        return { line: last.line, type: ValidationErrorType.Syntax };
      }
      stack.pop();
    } else {
      stack.push({ tag: tagName, line: lineNumber });
    }
  }

  if (stack.length > 0) {
    return { line: stack[stack.length - 1].line, type: ValidationErrorType.Syntax };
  }

  return firstPrintError ?? firstFieldError;
};

// --- CodeMirror extension ---

export const ERROR_LINE_CLASS = 'cm-error-line';
export const ERROR_LINE_GUTTER_CLASS = 'cm-error-line-gutter';

const errorLineEffect = StateEffect.define<{ pos: number } | null>();

const errorLineMark = Decoration.line({ class: ERROR_LINE_CLASS });

const errorLineField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    for (const effect of tr.effects) {
      if (effect.is(errorLineEffect)) {
        if (effect.value === null) {
          return Decoration.none;
        }
        const line = tr.state.doc.lineAt(effect.value.pos);
        return Decoration.set([errorLineMark.range(line.from)]);
      }
    }
    if (tr.docChanged) {
      return decorations.map(tr.changes);
    }
    return decorations;
  },
  provide: (f) => EditorView.decorations.from(f),
});

class ErrorGutterMarker extends GutterMarker {
  elementClass = ERROR_LINE_GUTTER_CLASS;
}

const errorGutterMark = new ErrorGutterMarker();

const errorGutterField = StateField.define<RangeSet<GutterMarker>>({
  create() {
    return RangeSet.empty;
  },
  update(markers, tr) {
    for (const effect of tr.effects) {
      if (effect.is(errorLineEffect)) {
        if (effect.value === null) {
          return RangeSet.empty;
        }
        const line = tr.state.doc.lineAt(effect.value.pos);
        return RangeSet.of([errorGutterMark.range(line.from)]);
      }
    }
    if (tr.docChanged) {
      return markers.map(tr.changes);
    }
    return markers;
  },
  provide: (f) => gutterLineClass.from(f),
});

export const clearErrorLineEffect = (view: EditorView) => {
  view.dispatch({
    effects: errorLineEffect.of(null),
  });
};

const highlightErrorLine = (view: EditorView, lineNumber: number | null): void => {
  if (lineNumber !== null && lineNumber >= 1 && lineNumber <= view.state.doc.lines) {
    const line = view.state.doc.line(lineNumber);
    view.dispatch({
      effects: errorLineEffect.of({ pos: line.from }),
    });
  } else {
    clearErrorLineEffect(view);
  }
};

export const validateApiError = (
  view: EditorView,
  onValidationChange: (error: HtmlValidationError | null) => void,
  errorType: ValidationErrorType,
  detail: string,
): HtmlValidationError | null => {
  const content = view.state.doc.toString();
  const lines = content.split('\n');

  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes(detail)) {
      const lineNumber = i + 1;
      const error: HtmlValidationError = {
        line: lineNumber,
        type: errorType,
        detail,
      };
      highlightErrorLine(view, lineNumber);
      onValidationChange(error);
      return error;
    }
  }

  // If we can't find the detail in the document, we can still report the error but won't be able to highlight a specific line
  const fallbackError: HtmlValidationError = {
    line: 0,
    type: errorType,
    detail,
  };
  clearErrorLineEffect(view);
  onValidationChange(fallbackError);
  return fallbackError;
};

export const runHtmlValidation = (
  view: EditorView,
  onValidationChange: (error: HtmlValidationError | null) => void,
  schema: TemplateSchema,
): HtmlValidationError | null => {
  const content = view.state.doc.toString();
  const error = validateHtml(content, schema);

  highlightErrorLine(view, error?.line ?? null);
  onValidationChange(error);
  return error;
};

export const createHtmlValidationExtension = ({ onValidationChange, schema }: HtmlValidationConfig): Extension[] => {
  const plugin = ViewPlugin.fromClass(
    class {
      private debounceTimer: ReturnType<typeof setTimeout> | null = null;

      constructor(view: EditorView) {
        this.scheduleValidation(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged) {
          this.scheduleValidation(update.view);
        }
      }

      private scheduleValidation(view: EditorView) {
        if (this.debounceTimer !== null) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => {
          runHtmlValidation(view, onValidationChange, schema);
        }, VALIDATION_DELAY_MS);
      }

      destroy() {
        if (this.debounceTimer !== null) {
          clearTimeout(this.debounceTimer);
        }
      }
    },
  );

  return [errorLineField, errorGutterField, plugin];
};
